import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addWithDecimals,
  decimalToFixed2,
  isGreaterOrEqualTo,
  isGreaterThan,
} from '../../../../config/utils/calcUtils';
import { CURRENCY } from '../../../../interfaces/enumerables';
import { useDocValues } from '../../../../services/hooks/useDocValues';
import { setToast } from '../../../../store/core/coreSlice';
import { useAppDispatch } from '../../../../store/hooks';

interface Props {
  formik: any;
}

export const DocProductsCost = ({ formik }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isReceipt = useMemo(
    () =>
      formik?.values?.documentTypeId?.object?.aadeCode === '11.2' ||
      formik?.values?.documentTypeId?.aadeCode === '11.2',
    [formik?.values?.documentTypeId],
  );
  const isCash = useMemo(
    () => formik?.values?.paymentTypeId?.object?.aadeCode === '3' || formik?.values?.paymentTypeId?.aadeCode === '3',
    [formik?.values?.paymentTypeId],
  );

  const taxWithholdPercentage = useMemo(
    () => formik?.values?.taxWithholdId?.object?.value || formik?.values?.taxWithholdId?.value || '0.00',
    [formik?.values?.taxWithholdId?.object?.value, formik?.values?.taxWithholdId?.value],
  );

  const docValues = useDocValues(formik.values?.products, 0, taxWithholdPercentage);

  const showError = useCallback(() => {
    dispatch(
      setToast({
        value: {
          show: true,
          msg: t('FORMS.ERRORS.VALUE_OVER_500'),
          isError: true,
          position: 'top',
          duration: 4000,
        },
      }),
    );
  }, [dispatch, t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      // cash not allowed for net value over 500
      if (isCash) {
        let total = decimalToFixed2(docValues.cleanValueOfProducts);
        if (isReceipt) {
          total = addWithDecimals(total, docValues.totalVat);
          if (isGreaterOrEqualTo(total, 500)) {
            showError();
            return;
          }
        }

        if (isGreaterThan(total, 500)) {
          showError();
        }
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [
    formik.values.documentTypeId,
    formik.values.paymentTypeId,
    isReceipt,
    isCash,
    showError,
    docValues.totalVat,
    docValues.cleanValueOfProducts,
  ]);

  useEffect(() => {
    formik.setFieldValue('totalValue', docValues?.cleanValueOfProducts);
  }, [docValues?.cleanValueOfProducts]);

  const getVatValuesView = useCallback(() => {
    return docValues?.valuesByVatCategory?.map?.((cat, index) => {
      return (
        <div className="flex-space-between-start-row-nowrap width-100" key={`${cat.percentage}-${index}`}>
          <div className="width-60px">{cat.percentage}%</div>
          <div className="width-180px textAlignEnd">
            {decimalToFixed2(cat.cleanCost)} {CURRENCY.EUR}
          </div>
          <div className="width-120px textAlignEnd">
            {decimalToFixed2(cat.discount)} {CURRENCY.EUR}
          </div>
          <div className="width-180px textAlignEnd">
            {decimalToFixed2(cat.finalCleanCost)} {CURRENCY.EUR}
          </div>
          <div className="width-120px textAlignEnd">
            {decimalToFixed2(cat.total)} {CURRENCY.EUR}
          </div>
        </div>
      );
    });
  }, [docValues?.valuesByVatCategory]);

  const getTaxWithholdSection = useCallback(() => {
    if (docValues?.taxWithholdAmount !== '0.00') {
      return (
        <div className="flex-space-between-start-row-nowrap width-100 padding-t-6">
          <div className="boldText5">{t('SALE.TAX_WITHHOLD')}</div>
          <div className="boldText5">
            -{docValues?.taxWithholdAmount} {CURRENCY.EUR}
          </div>
        </div>
      );
    }
  }, [docValues?.taxWithholdAmount, t]);

  return (
    <div className="flex-center-start-column-nowrap min-width-460px">
      <div className="flex-space-between-start-row-nowrap width-100 padding-b-4">
        <div className="width-60px boldText5">{t('SALE.VAT')} %</div>
        <div className="width-180px textAlignEnd boldText5">{t('SALE.CLEAN_COST_BEFORE_DISCOUNT')}</div>
        <div className="width-120px textAlignEnd boldText5">{t('SALE.DISCOUNT')}</div>
        <div className="width-180px textAlignEnd boldText5">{t('SALE.CLEAN_COST')}</div>
        <div className="width-120px textAlignEnd boldText5">
          {t('SALE.VAT')} {CURRENCY.EUR}
        </div>
      </div>
      {getVatValuesView()}
      <div className="g-separator margin-t-8 margin-b-8" />
      <div className="flex-space-between-start-row-nowrap width-100 padding-b-6">
        <div>{t('SALE.TOTAL_CLEAN_VALUE')}</div>
        <div>{`${docValues?.cleanValueOfProducts} ${CURRENCY.EUR}`}</div>
      </div>
      <div className="flex-space-between-start-row-nowrap width-100 padding-b-6">
        <div>{t('SALE.TOTAL_VAT')}</div>
        <div>{`${docValues?.totalVat} ${CURRENCY.EUR}`}</div>
      </div>
      <div className="flex-space-between-start-row-nowrap width-100">
        <div className="boldText5">{t('SALE.TOTAL')}</div>
        <div className="boldText5">
          {docValues?.totalGrossAmount} {CURRENCY.EUR}
        </div>
      </div>
      {getTaxWithholdSection()}
      <div className="flex-space-between-start-row-nowrap width-100 margin-t-40">
        <div className="boldText5">{t('SALE.PAYABLE_AMOUNT')}</div>
        <div className="boldText5">
          {docValues.payableAmount} {CURRENCY.EUR}
        </div>
      </div>
    </div>
  );
};
